import { DestroyRef, Directive, ElementRef, inject, input, output } from '@angular/core';

import { triggerHapticImpact } from '../haptics';

const MOVE_TOLERANCE_PX = 10;

@Directive({
  selector: '[tlmLongPress]',
  host: {
    '(pointerdown)': 'onPointerDown($event)',
    '(pointermove)': 'onPointerMove($event)',
    '(pointerup)': 'cancelPress()',
    '(pointercancel)': 'cancelPress()',
    '(touchend)': 'onTouchEnd($event)',
    '(contextmenu)': 'onContextMenu($event)',
    '[style.-webkit-touch-callout]': "tlmLongPressDisabled() ? null : 'none'",
    '[style.-webkit-user-select]': "tlmLongPressDisabled() ? null : 'none'",
    '[style.user-select]': "tlmLongPressDisabled() ? null : 'none'",
  },
})
export class TellentMobileLongPressDirective {
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  public readonly tlmLongPressDisabled = input(false);
  public readonly tlmLongPressDuration = input(500);
  public readonly tlmLongPress = output<void>();

  private timeout: number | null = null;
  private startX = 0;
  private startY = 0;
  private fired = false;

  constructor() {
    // The pointerup that ends a long press still produces a click; swallow it
    // before it reaches the host's own click handlers. Capture phase, since
    // host listeners have no capture option.
    const suppressClick = (event: Event) => {
      if (this.fired) {
        this.fired = false;
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    const element = <HTMLElement>this.elementRef.nativeElement;
    element.addEventListener('click', suppressClick, true);
    inject(DestroyRef).onDestroy(() => {
      this.cancelPress();
      element.removeEventListener('click', suppressClick, true);
    });
  }

  protected onPointerDown(event: PointerEvent): void {
    if (this.tlmLongPressDisabled() || event.isPrimary === false) {
      return;
    }
    this.cancelPress();
    this.fired = false;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.timeout = window.setTimeout(() => {
      this.timeout = null;
      this.fired = true;
      triggerHapticImpact();
      this.tlmLongPress.emit();
    }, this.tlmLongPressDuration());
  }

  protected onPointerMove(event: PointerEvent): void {
    if (
      this.timeout !== null &&
      (Math.abs(event.clientX - this.startX) > MOVE_TOLERANCE_PX ||
        Math.abs(event.clientY - this.startY) > MOVE_TOLERANCE_PX)
    ) {
      this.cancelPress();
    }
  }

  protected onTouchEnd(event: TouchEvent): void {
    // After touchend the browser synthesizes compat mouse events (mousedown,
    // mouseup, click) hit-tested at the lift point. If the long press opened
    // an overlay, that click lands on its backdrop and dismisses it, so stop
    // the synthesis at the source. The capture-phase click suppressor keeps
    // covering mouse input and non-cancelable touchends.
    if (this.fired && event.cancelable) {
      event.preventDefault();
      this.fired = false;
    }
  }

  protected onContextMenu(event: Event): void {
    if (!this.tlmLongPressDisabled()) {
      event.preventDefault();
    }
  }

  protected cancelPress(): void {
    if (this.timeout !== null) {
      window.clearTimeout(this.timeout);
      this.timeout = null;
    }
  }
}
